import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Field, Input, Label, Select, Textarea } from '../../ui';
import { adminLocationService } from '../../../services/locations';
import { LocationRulesPreview } from '../../../types/location';
import { LocationForm } from './locationForm';
import { PairListEditor, TextListEditor } from './ListEditors';

const Group = styled.fieldset`
  display: grid;
  gap: 1rem;
  padding: 1.25rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: #fff;

  legend {
    padding: 0 0.4rem;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const Hint = styled.small`
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.45;
`;

const Preview = styled.div`
  padding: 0.9rem 1rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.primarySoft};
  font-size: 0.9rem;

  strong { font-size: 1.05rem; }
  ul { margin-top: 0.5rem; display: grid; gap: 0.2rem; color: ${({ theme }) => theme.colors.neutral[700]}; }
  li { font-size: 0.82rem; }
`;

const RulesPreview: React.FC<{ form: LocationForm }> = ({ form }) => {
  const [preview, setPreview] = useState<LocationRulesPreview | null>(null);
  const [failed, setFailed] = useState(false);
  const rulesKey = JSON.stringify([form.neighborhoods, form.title_terms, form.city.trim()]);

  useEffect(() => {
    let active = true;
    const [neighborhoods, title_terms, city] = JSON.parse(rulesKey);
    const timer = window.setTimeout(() => {
      adminLocationService
        .preview({ neighborhoods, title_terms, city: city || null })
        .then((data) => { if (active) { setPreview(data); setFailed(false); } })
        .catch(() => { if (active) setFailed(true); });
    }, 400);
    return () => { active = false; window.clearTimeout(timer); };
  }, [rulesKey]);

  if (failed) return <Preview>Não foi possível calcular os imóveis agora.</Preview>;
  if (!preview) return <Preview>Calculando imóveis…</Preview>;
  return (
    <Preview aria-live="polite">
      <strong>{preview.total}</strong> {preview.total === 1 ? 'imóvel disponível casa' : 'imóveis disponíveis casam'} com as regras
      {' '}({preview.sale_count} à venda, {preview.rental_count} para alugar)
      {preview.sample.length > 0 && (
        <ul>
          {preview.sample.map((item) => (
            <li key={item.id}>{item.title}{item.neighborhood ? ` · ${item.neighborhood}` : ''}</li>
          ))}
        </ul>
      )}
    </Preview>
  );
};

interface Props {
  form: LocationForm;
  onChange: (form: LocationForm) => void;
  suggestions: string[];
}

const LocationFormFields: React.FC<Props> = ({ form, onChange, suggestions }) => {
  const set = <K extends keyof LocationForm>(key: K, value: LocationForm[K]) =>
    onChange({ ...form, [key]: value });
  const text = (key: keyof LocationForm) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    set(key, event.target.value as never);

  return (
    <>
      <Group>
        <legend>Identificação</legend>
        <Row>
          <Field>
            <Label htmlFor="loc-name">Nome</Label>
            <Input id="loc-name" value={form.name} onChange={text('name')} required />
          </Field>
          <Field>
            <Label htmlFor="loc-kind">Tipo</Label>
            <Select id="loc-kind" value={form.kind} onChange={(event) => set('kind', event.target.value as LocationForm['kind'])}>
              <option value="condominio">Condomínio</option>
              <option value="edificio">Edifício</option>
              <option value="bairro">Bairro</option>
            </Select>
          </Field>
        </Row>
        <Row>
          <Field>
            <Label htmlFor="loc-slug">Endereço da página</Label>
            <Input id="loc-slug" value={form.slug} onChange={text('slug')} placeholder="gerado a partir do nome" />
            <Hint>Letras minúsculas, números e hífen. Vazio: gerado do nome.</Hint>
          </Field>
          <Field>
            <Label htmlFor="loc-city">Cidade</Label>
            <Input id="loc-city" value={form.city} onChange={text('city')} placeholder="opcional" />
            <Hint>Restringe os imóveis a esta cidade (útil para bairros com nome comum).</Hint>
          </Field>
        </Row>
      </Group>

      <Group>
        <legend>Quais imóveis aparecem</legend>
        <Field>
          <Label htmlFor="loc-neighborhoods">Bairros ou condomínios do cadastro</Label>
          <TextListEditor
            id="loc-neighborhoods"
            values={form.neighborhoods}
            onChange={(values) => set('neighborhoods', values)}
            placeholder="Ex.: Parque Residencial Buona Vita"
            suggestions={suggestions}
          />
          <Hint>Casa com o bairro (ou nome do condomínio) do imóvel, sem diferenciar acento ou maiúscula.</Hint>
        </Field>
        <Field>
          <Label htmlFor="loc-terms">Termos no título</Label>
          <TextListEditor
            id="loc-terms"
            values={form.title_terms}
            onChange={(values) => set('title_terms', values)}
            placeholder="Ex.: Borghese"
          />
          <Hint>Palavra inteira no título do anúncio. Use para edifícios e condomínios que não são bairro.</Hint>
        </Field>
        <RulesPreview form={form} />
      </Group>

      <Group>
        <legend>Conteúdo</legend>
        <Field>
          <Label htmlFor="loc-headline">Chamada (abaixo do nome)</Label>
          <Input id="loc-headline" value={form.headline} onChange={text('headline')} maxLength={240} />
        </Field>
        <Field>
          <Label htmlFor="loc-intro">Apresentação</Label>
          <Textarea id="loc-intro" rows={8} value={form.intro} onChange={text('intro')} />
          <Hint>Separe os parágrafos com uma linha em branco.</Hint>
        </Field>
        <Field>
          <Label as="span">Destaques</Label>
          <PairListEditor
            items={form.highlights}
            onChange={(items) => set('highlights', items)}
            fields={[
              { key: 'title', placeholder: 'Título' },
              { key: 'text', placeholder: 'Texto', multiline: true },
            ]}
            addLabel="Adicionar destaque"
            blank={{ title: '', text: '' }}
          />
        </Field>
        <Field>
          <Label as="span">Perguntas frequentes</Label>
          <PairListEditor
            items={form.faq}
            onChange={(items) => set('faq', items)}
            fields={[
              { key: 'question', placeholder: 'Pergunta' },
              { key: 'answer', placeholder: 'Resposta', multiline: true },
            ]}
            addLabel="Adicionar pergunta"
            blank={{ question: '', answer: '' }}
          />
        </Field>
      </Group>

      <Group>
        <legend>Foto, mapa e SEO</legend>
        <Field>
          <Label htmlFor="loc-hero">Foto de capa (URL)</Label>
          <Input id="loc-hero" value={form.hero_image_url} onChange={text('hero_image_url')} placeholder="vazio: melhor foto dos imóveis do local" />
        </Field>
        <Row>
          <Field>
            <Label htmlFor="loc-lat">Latitude</Label>
            <Input id="loc-lat" inputMode="decimal" value={form.latitude} onChange={text('latitude')} placeholder="vazio: centro dos imóveis" />
          </Field>
          <Field>
            <Label htmlFor="loc-lng">Longitude</Label>
            <Input id="loc-lng" inputMode="decimal" value={form.longitude} onChange={text('longitude')} placeholder="vazio: centro dos imóveis" />
          </Field>
          <Field>
            <Label htmlFor="loc-order">Ordem</Label>
            <Input id="loc-order" inputMode="numeric" value={form.sort_order} onChange={text('sort_order')} />
          </Field>
        </Row>
        <Field>
          <Label htmlFor="loc-seo-title">Título para o Google</Label>
          <Input id="loc-seo-title" value={form.seo_title} onChange={text('seo_title')} maxLength={160} placeholder="vazio: nome + imóveis à venda e para alugar" />
        </Field>
        <Field>
          <Label htmlFor="loc-seo-desc">Descrição para o Google</Label>
          <Textarea id="loc-seo-desc" rows={2} value={form.seo_description} onChange={text('seo_description')} maxLength={320} placeholder="vazio: usa a chamada" />
        </Field>
      </Group>
    </>
  );
};

export default LocationFormFields;
